/**
 * API Client — wrapper fetch natif
 * Gère les headers JSON, l'access token, et le refresh automatique.
 */

const API_BASE = '/api/v1';

/**
 * Construit l'URL absolue d'un endpoint.
 * On passe par location.origin (qui ne porte JAMAIS les credentials) plutôt
 * qu'un chemin relatif : si l'utilisateur accède au site avec un BasicAuth dans
 * l'URL (https://user:pass@host), fetch refuse une requête relative résolue sur
 * une URL avec credentials ("Request cannot be constructed from a URL that
 * includes credentials"). location.origin élimine ce piège.
 */
function apiUrl(path) {
  return `${window.location.origin}${API_BASE}${path}`;
}

let accessToken = null;

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

/**
 * Erreur API avec code métier
 */
export class ApiError extends Error {
  constructor(message, code, status) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

/**
 * Tente un refresh du token via le cookie HTTP-only
 */
async function refreshAccessToken() {
  try {
    const res = await fetch(apiUrl('/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data = await res.json();
    accessToken = data.data.accessToken;
    return true;
  } catch {
    return false;
  }
}

/**
 * Requête HTTP générique
 */
async function request(path, options = {}) {
  const { body, isFormData, _retry, ...rest } = options;

  const headers = {};
  if (!isFormData) headers['Content-Type'] = 'application/json';
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const config = {
    ...rest,
    headers: { ...headers, ...options.headers },
    credentials: 'include',
  };

  if (body !== undefined) {
    config.body = isFormData ? body : JSON.stringify(body);
  }

  const response = await fetch(apiUrl(path), config);

  // Gestion du 401 — auto-refresh une seule fois
  if (response.status === 401 && !_retry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return request(path, { ...options, _retry: true });
    }
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new ApiError(
      'Réponse invalide du serveur',
      'INVALID_RESPONSE',
      response.status
    );
  }

  if (!response.ok) {
    throw new ApiError(
      data.error?.message || 'Une erreur est survenue',
      data.error?.code,
      response.status
    );
  }

  return data;
}

/**
 * Télécharge un fichier protégé (auth Bearer) et renvoie un Blob.
 * Les endpoints de download streament le binaire via l'API ; on ne peut pas
 * utiliser un simple <a href> car le token JWT passe par le header Authorization.
 */
async function requestBlob(path, options = {}) {
  const { _retry, ...rest } = options;

  const headers = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const response = await fetch(apiUrl(path), {
    ...rest,
    method: 'GET',
    headers: { ...headers, ...options.headers },
    credentials: 'include',
  });

  if (response.status === 401 && !_retry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return requestBlob(path, { ...options, _retry: true });
  }

  if (!response.ok) {
    throw new ApiError('Téléchargement échoué', 'DOWNLOAD_FAILED', response.status);
  }

  return response.blob();
}

export const api = {
  get: (path) => request(path, { method: 'GET' }),
  download: (path) => requestBlob(path),
  post: (path, body) => request(path, { method: 'POST', body }),
  put: (path, body) => request(path, { method: 'PUT', body }),
  patch: (path, body) => request(path, { method: 'PATCH', body }),
  delete: (path, body) => request(path, { method: 'DELETE', ...(body ? { body } : {}) }),
  upload: (path, formData) =>
    request(path, { method: 'POST', body: formData, isFormData: true }),
};

export default api;
